import { connect } from 'react-redux';
import { compose } from 'redux';
import { withAssetTypesConfiguration } from '@mulesoft/exchange-react-hooks';
import RootStoreContext from '~/contexts/RootStoreContext';
import {
  actions as searchActions,
  selectors as searchSelectors
} from '~/domains/search';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import searchExperienceContextSelector from '~/analytics/contexts/selectors/searchExperience';
import { actions as analyticsActions } from '~/domains/analytics';
import ANGFilters from '~/utils/ang/ANGFilters';
import async from '~/utils/async';
import { EVENT_TYPES } from '~/analytics/events';
import { parseSearch } from '~/utils/location';
import { getActiveOrganization } from '~/utils/organization';
import * as objectsUtils from '~/utils/objects';
import HomeLayout from './HomeLayout';
import resolver, {
  trackSearchExecuted,
  buildHeader
} from './HomeLayout.resolver';
import { isMasterOrganizationExchangeAdmin } from '~/utils/validations/profile';

const mapStateToProps = (state, { location, assetTypesConfiguration }) => {
  const profile = commonSelectors.profile(state);
  const query = parseSearch(location.search);
  const rootOrganizations = searchSelectors.rootOrganizations(state);
  const filters = ANGFilters.buildFromQuery(
    query,
    assetTypesConfiguration,
    profile,
    rootOrganizations
  );
  const boostConfiguration = ANGFilters.getBoostConfiguration(query);
  const isANGQuery = searchSelectors.isANGSearchEnabled(state);
  const angConfig = searchSelectors.angConfig(state, {
    sort: query.sort,
    show: query.show,
    search: query.search,
    boostConfiguration,
    assetTypesConfiguration,
    includeEmptyFilters: true
  });
  const hasHomeQueryChanged = commonSelectors.hasHomeQueryChanged(state, {
    query
  });
  const isSearchTrigger = !!hasHomeQueryChanged;
  const isANGAvailable = searchSelectors.isANGAvailable(state);

  return {
    profile,
    angConfig,
    filters,
    isMasterOrgAdmin: isMasterOrganizationExchangeAdmin(profile),
    shouldExecuteSearch: isSearchTrigger && (isANGQuery || !isANGAvailable),
    query,
    isANGQuery
  };
};

const searchAssets = (
  filters,
  angConfig,
  trackProps,
  assetTypesConfiguration
) => {
  return async (dispatch, getState) => {
    dispatch(commonActions.setHomeQuery(trackProps.query));
    dispatch(
      commonActions.setHeader(
        buildHeader(trackProps.query, assetTypesConfiguration)
      )
    );
    await dispatch(searchActions.search(filters, angConfig));

    const trackEvent = (eventSchema, properties) => {
      dispatch(
        analyticsActions.trackEvent(eventSchema, {
          ...properties,
          context: searchExperienceContextSelector(getState())
        })
      );
    };

    trackSearchExecuted({
      ...trackProps,
      trackEvent,
      getState,
      assetTypesConfiguration
    });
  };
};

const mapDispatchToProps = (dispatch, { assetTypesConfiguration }) => ({
  onPageLoaded: (properties) =>
    dispatch(
      analyticsActions.trackEvent(EVENT_TYPES.HOME_PAGE_LOADED, properties)
    ),
  onMasterOrgPageLoaded: () =>
    dispatch(
      analyticsActions.trackEvent(
        EVENT_TYPES.HOME_PAGE_LOADED_BY_MASTER_ORG_ADMIN
      )
    ),
  search: (angConfig, filters, query, isANGQuery) =>
    dispatch(
      searchAssets(
        filters,
        angConfig,
        {
          query,
          isANGQuery
        },
        assetTypesConfiguration
      )
    )
});

const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
  search: () =>
    dispatchProps.search(
      stateProps.angConfig,
      stateProps.filters,
      stateProps.query,
      stateProps.isANGQuery
    ),
  onPageLoaded: () => {
    let sessionProperties = {};

    if (stateProps.profile) {
      const activeOrganization = getActiveOrganization(stateProps.profile);

      if (activeOrganization) {
        sessionProperties = objectsUtils.pick(
          activeOrganization,
          'isExchangeAdmin',
          'isExchangeContributor'
        );
      }
    }

    dispatchProps.onPageLoaded(sessionProperties);
  }
});

export default compose(
  async(resolver, { context: RootStoreContext }),
  withAssetTypesConfiguration,
  connect(mapStateToProps, mapDispatchToProps, mergeProps, {
    context: RootStoreContext
  })
)(HomeLayout);
